import Input from "../Common/Forms/Input";
import ButtonPrimary from "../Common/Buttons/ButtonPrimary";
import { globalStores } from "../api/globalStores";
import { useState } from "react";
import toast from "react-hot-toast";
import { observer } from "mobx-react";
import { FlowElementProps } from "./OnboardingFlow";
import { useNavigate } from "react-router-dom";
import useCountryCity from "../Common/Forms/Helpers/useCountryCity";
import child from "../api/child";

export const ChildDetails = observer(() => {
  const navigate = useNavigate();
  const [isValidPasscode, setIsValidPasscode] = useState(false);
  const [toastId, setToastId] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [childDetails, setChildDetails] = useState<{
    firstName?: string;
    lastName?: string;
    birthday?: Date;
    passCode?: number;
    city?: string;
    childSchool?: string;
  }>({});

  const { possibleCountries, possibleCities, validCity, validCountry } =
    useCountryCity(country ?? "", childDetails.city ?? "");

  const submitChildDetails = async () => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    
    try {
      if (!childDetails.firstName) {
        throw new Error("Please enter a valid first name!");
      }
      if (!childDetails.lastName) {
        throw new Error("Please enter a valid last name!");
      }
      if (!childDetails.birthday) {
        throw new Error("Please enter a valid birthday!");
      }
      if (!childDetails.passCode || !isValidPasscode) {
        throw new Error("Please enter a valid 4-digit pass code!");
      }
      if (!childDetails.city || !validCity) {
        throw new Error("Please enter a valid city!");
      }
      if (!childDetails.childSchool) {
        throw new Error("Please enter a valid school!");
      }

      await globalStores.parentStore.createChild(
        childDetails.firstName,
        childDetails.lastName,
        childDetails.birthday,
        childDetails.passCode,
        childDetails.city,
        childDetails.childSchool
      );
      navigate("/parent/flow/child_avatar_details");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <p className="text-xl pb-4 pl-3 text-center">
        Please input your <b>Child</b> details
      </p>
      <Input
        onChange={(firstName) =>
          setChildDetails({ ...childDetails, firstName })
        }
        type="text"
        placeholder="Child's First Name"
        icon="user"
      />
      <Input
        onChange={(lastName) => setChildDetails({ ...childDetails, lastName })}
        default={childDetails.lastName}
        type="text"
        placeholder="Child's Last Name"
        icon="user"
      />
      {/* TODO: Add Birthday display to date selector */}
      <Input
        onChange={(birthday) => {
          setChildDetails({
            ...childDetails,
            birthday:
              birthday && birthday !== "" ? new Date(birthday) : undefined,
          });
          console.log(
            `On change has ran with input of: ${birthday} of type ${typeof birthday}`
          );
        }}
        type="date"
        value={childDetails.birthday}
        placeholder="Birthday"
        icon="calendar"
      />
      <Input
        onChange={(passCode) => {
          if (!passCode.match(/^[0-9]+$/)) {
            const newId = toast.error(
              "Passcode must be all digits, and 4 characters long!",
              { id: toastId }
            );
            setToastId(newId);
            setIsValidPasscode(false);
            return;
          }
          if (passCode.length !== 4) {
            setIsValidPasscode(false);
            return;
          }
          setIsValidPasscode(true);
          setChildDetails({ ...childDetails, passCode: +passCode });
        }}
        type="passcode"
        placeholder="Create a Passcode for this Child (4 Digits)"
      />
      <Input
        value={country}
        default={country}
        onChange={(originCountry) => setCountry(originCountry)}
        type="autocomplete"
        placeholder="Country"
        options={possibleCountries.map((country) => ({
          value: country,
          label: country,
          handleLabel: () => {},
          handleValue: () => setCountry(country),
        }))}
        icon="globe"
      />
      <Input
        value={childDetails.city}
        default={childDetails.city}
        onChange={(city) => setChildDetails({ ...childDetails, city })}
        options={possibleCities.map((city) => ({
          value: city,
          label: city,
          handleLabel: () => {},
          handleValue: () => setChildDetails({ ...childDetails, city }),
        }))}
        type="autocomplete"
        placeholder="City of Residence"
        icon="city"
      />

      <Input
        onChange={(childSchool: string) => {
          if (childSchool.length <= 140) {
            setChildDetails({ ...childDetails, childSchool });
            console.log(childSchool.length);
          } else {
            toast.error("Maximum of 140 chars for Child's School reached!");
          }
        }}
        value={childDetails.childSchool}
        type="text"
        placeholder="Child's School Name"
        icon="book"
      />

      <ButtonPrimary
        onClick={() => {
          if (isSubmitting) return;
          toast.promise(submitChildDetails(), {
            loading: "Submitting Child Details",
            error: (e: any) => e.message,
            success: "Child Details Updated!",
          });
        }}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Creating..." : "Create"}
      </ButtonPrimary>
      {/* <div className="flex justify-center py-4 px-2">
        <button
          onClick={() => navigate("/parent/dashboard")}
          className="text-brij-DarkGrey hover:text-brij-Black"
        >
          BACK
        </button>
      </div> */}
    </>
  );
});
